import { useCallback, useMemo, useState } from "react";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import ProgressBar from "ds/components/ProgressBar";
import Link from "ds/components/Link";
import { QuestionMarkCircle } from "components/icons/generated";
import Card from "ds/components/Card";
import Banner from "ds/components/Banner";
import TextEllipsis from "ds/components/TextEllipsis";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";
import { AssistantPanelMode } from "components/AssistantPanel/Layout/types";
import useAssistantPanelStore from "components/AssistantPanel/Layout/useStore";
import useUserGuideProgressMutations from "shared/UserGuides/useUserGuideProgressMutations";
import { UserGuideStepData } from "shared/UserGuides/gql";
import {
  getHardcodedUserGuideGroupIconAndColor,
  randomIconAndColor,
} from "shared/UserGuides/mocks";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageUserGuides } from "hooks/useAnalytics/pages/userGuides";

import useAssistantPanelLearnStore from "../../../useStore";
import styles from "./styles.module.css";

type AssistantPanelLearnActiveGuideViewStepProps = {
  guideTitle: string;
  currentStep: UserGuideStepData;
  currentStepIndex: number;
  totalSteps: number;
  onCompleteClick: () => void;
};

const AssistantPanelLearnActiveGuideViewStep = ({
  guideTitle,
  currentStep,
  currentStepIndex,
  totalSteps,
  onCompleteClick,
}: AssistantPanelLearnActiveGuideViewStepProps) => {
  const { setNavigationState, navigationState } = useAssistantPanelLearnStore();
  const { switchMode } = useAssistantPanelStore();
  const { moveToNextStep, completeUserGuide, abandonUserGuide } = useUserGuideProgressMutations();
  const trackEvent = useAnalytics({ page: AnalyticsPageUserGuides.UserGuides });

  const [isCancelling, setIsCancelling] = useState(false);
  const [isNextStepLoading, setIsNextStepLoading] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const isAnyActionLoading = isCancelling || isNextStepLoading || isCompleting;
  const isLastStep = currentStepIndex + 1 === totalSteps;

  const handleCancel = useCallback(async () => {
    setIsCancelling(true);
    try {
      await abandonUserGuide();
      if (navigationState.level === "guide") {
        setNavigationState({ level: "chapters", groupId: navigationState.groupId });
      } else {
        setNavigationState({ level: "groups" });
      }
      setNavigationState({ level: "groups" });
    } finally {
      setIsCancelling(false);
    }
  }, [setNavigationState, abandonUserGuide, navigationState]);

  const handleNextStep = useCallback(async () => {
    setIsNextStepLoading(true);
    try {
      await moveToNextStep();
    } finally {
      setIsNextStepLoading(false);
    }
  }, [moveToNextStep]);

  const handleCompleteGuide = useCallback(async () => {
    setIsCompleting(true);
    onCompleteClick();

    try {
      await completeUserGuide();
    } finally {
      setIsCompleting(false);
    }
  }, [completeUserGuide, onCompleteClick]);

  const handleNeedHelp = useCallback(() => {
    trackEvent("Need Help Clicked", {
      userGuideName: guideTitle,
      userGuideStep: currentStep.title,
    });
    switchMode(AssistantPanelMode.AI_CHAT);
  }, [switchMode, trackEvent, guideTitle, currentStep.title]);

  const icon = useMemo(() => {
    if (navigationState.level === "guide" || navigationState.level === "chapters") {
      const { groupId } = navigationState;
      if (groupId) {
        return getHardcodedUserGuideGroupIconAndColor(groupId).icon;
      }
    }

    // if from some reason we are in the groups level, fallback to a random icon and color
    return randomIconAndColor().icon;
  }, [navigationState]);

  return (
    <Box direction="column" gap="large" grow="1" padding="x-large" scrollable>
      <Box align="center" justify="between" fullWidth gap="medium">
        <Box align="center" gap="small" className={styles.guideTitle} padding="small">
          <Icon src={icon} />
          <TextEllipsis tooltip={guideTitle} tooltipWidthMode="maxWidthXl">
            {(props) => (
              <Typography {...props} tag="span" variant="p-body4">
                {guideTitle}
              </Typography>
            )}
          </TextEllipsis>
        </Box>

        <Box grow="1" align="center" gap="medium" className={styles.progressBar}>
          <ProgressBar
            current={currentStepIndex + 1}
            max={totalSteps}
            fullWidth
            ariaLabel="Guide progress"
          />
        </Box>
      </Box>

      <Card contentGap="large" className={styles.card}>
        <Typography tag="h3" variant="p-t7">
          {currentStep.title}
        </Typography>

        <Typography tag="div" variant="p-body3">
          <MarkdownAssistantPanel>{currentStep.instruction}</MarkdownAssistantPanel>
        </Typography>

        {currentStep.hint && (
          <Banner variant="info">
            <MarkdownAssistantPanel>{currentStep.hint}</MarkdownAssistantPanel>
          </Banner>
        )}

        <Box direction="row" gap="medium" align="center">
          <Button
            variant="secondary"
            size="small"
            onPress={handleCancel}
            disabled={isAnyActionLoading}
            loading={isCancelling}
          >
            Cancel
          </Button>
          {!isLastStep && (
            <Button
              variant="primary"
              size="small"
              onPress={handleNextStep}
              disabled={isAnyActionLoading}
              loading={isNextStepLoading}
            >
              Next step
            </Button>
          )}

          {isLastStep && (
            <Button
              variant="primary"
              size="small"
              onPress={handleCompleteGuide}
              disabled={isAnyActionLoading}
              loading={isCompleting}
            >
              Complete
            </Button>
          )}
        </Box>

        <Box
          align="center"
          justify="center"
          gap="small"
          padding="large 0 0 0"
          className={styles.needHelpRow}
        >
          <Icon src={QuestionMarkCircle} className={styles.helpIcon} />
          <Link variant="secondary" size="small" onPress={handleNeedHelp}>
            Need help with this step?
          </Link>
        </Box>
      </Card>
    </Box>
  );
};

export default AssistantPanelLearnActiveGuideViewStep;
